import { criterion, fail, fromCriteria } from "@/core/check";
import type { Lesson } from "@/core/types";
import { type CellInfo, hasFormula, isText, readWorkbook } from "@/office/excel/workbook";
import { nichtPruefbar } from "../schnittstelle";

/** Zeilen, in denen mindestens `breite` feste Textzellen direkt nebeneinander stehen. */
function zeilenMitTextblock(zellen: CellInfo[], breite: number): number {
  const nachZeile = new Map<number, CellInfo[]>();
  for (const zelle of zellen) {
    if (!isText(zelle) || hasFormula(zelle)) continue;
    const liste = nachZeile.get(zelle.row) ?? [];
    liste.push(zelle);
    nachZeile.set(zelle.row, liste);
  }
  let treffer = 0;
  for (const liste of nachZeile.values()) {
    const spalten = liste.map((zelle) => zelle.column).sort((a, b) => a - b);
    let lauf = 1;
    let bester = 1;
    for (let i = 1; i < spalten.length; i++) {
      lauf = spalten[i] === spalten[i - 1] + 1 ? lauf + 1 : 1;
      bester = Math.max(bester, lauf);
    }
    if (bester >= breite) treffer += 1;
  }
  return treffer;
}

export const lektionTextInSpalten: Lesson = {
  id: "text-in-spalten",
  title: "Text in Spalten",
  summary: "Aus einer Spalte „Weber;Anna;Berlin“ werden drei saubere Spalten.",
  durationMinutes: 9,
  learningGoals: [
    "Den Assistenten „Text in Spalten“ mit Trennzeichen bedienen",
    "Den Datentyp einzelner Spalten beim Trennen festlegen",
    "Wissen, wann Formeln oder Power Query die bessere Wahl sind",
  ],
  steps: [
    {
      id: "trennen",
      title: "Mit Trennzeichen trennen",
      explanation: [
        "Exporte aus anderen Programmen landen oft in einer einzigen Spalte: " +
          "„Weber;Anna;Berlin“. „Daten“ → „Text in Spalten“ teilt sie auf. Im ersten Schritt " +
          "wählst du „Getrennt“, im zweiten das Trennzeichen – Semikolon, Komma, Tabstopp " +
          "oder ein eigenes Zeichen. Die Vorschau zeigt sofort, wie die Spalten aussehen " +
          "werden.",
        "Der dritte Schritt wird gern übersprungen und ist der wichtigste: Dort legst du für " +
          "jede Spalte den Datentyp fest. Postleitzahlen und Artikelnummern mit führender " +
          "Null gehören auf „Text“, sonst wird aus 01067 die Zahl 1067. Unter „Ziel“ lässt " +
          "sich außerdem angeben, wohin das Ergebnis geschrieben wird – sonst überschreibt " +
          "Excel die Nachbarspalten ohne Rückfrage.",
      ],
      keyPoints: [
        "Daten → Text in Spalten → Getrennt → Trennzeichen.",
        "Schritt 3: Spalten mit führender Null als „Text“.",
        "Ziel angeben, damit keine Nachbarspalte überschrieben wird.",
      ],
      task: {
        instruction:
          "Schreibe mindestens drei Zeilen wie „Weber;Anna;Berlin“ untereinander in eine " +
          "Spalte und teile sie mit „Text in Spalten“ in drei Spalten auf.",
        hint: "Spalte markieren, Daten → Text in Spalten → Getrennt → Semikolon → Fertig stellen.",
        check: async () => {
          const mappe = await readWorkbook();
          if (!mappe.features.cells) {
            return nichtPruefbar("Die Zellinhalte", "1.4", "Sieh selbst nach, ob die Werte in drei Spalten stehen.", true);
          }
          const zellen = mappe.sheets.flatMap((sheet) => sheet.cells);
          if (zellen.every((zelle) => zelle.valueType === "Empty")) {
            return fail("In der Arbeitsmappe steht noch nichts.", "Schreib zuerst die Beispielzeilen.");
          }
          const unverteilt = zellen.filter((zelle) => isText(zelle) && /;/.test(String(zelle.value)));
          const bloecke = zeilenMitTextblock(zellen, 3);
          return fromCriteria(
            [
              criterion(`Keine Zelle enthält mehr ein Semikolon (gefunden: ${unverteilt.length})`, unverteilt.length === 0),
              criterion(`Mindestens drei Zeilen mit drei Textspalten nebeneinander (gefunden: ${bloecke})`, bloecke >= 3),
            ],
            {
              success: "Aus einer Spalte sind drei geworden – ohne ein Zeichen abzutippen.",
              failure: "Die Daten sind noch nicht aufgeteilt.",
              hint: "Nur die Spalte mit den Rohdaten markieren, dann Text in Spalten.",
            },
          );
        },
      },
    },
    {
      id: "wann-was",
      title: "Text in Spalten, Formel oder Power Query?",
      explanation: [
        "Text in Spalten erzeugt feste Werte – einmal ausgeführt, fertig. Das passt für " +
          "einen einmaligen Import. Kommt derselbe Export jede Woche, ist Power Query der " +
          "bessere Weg (nächste Lektion): Die Schritte werden gespeichert und laufen mit " +
          "„Aktualisieren“ erneut.",
        "Soll die Aufteilung lebendig bleiben, weil die Quelldaten in der Mappe gepflegt " +
          "werden, gehören Formeln dorthin – TEXTTEILEN in Microsoft 365, sonst TEXTVOR, " +
          "TEXTNACH oder LINKS mit FINDEN (Modul „Weitere Funktionen“).",
      ],
      keyPoints: [
        "Einmalig: Text in Spalten.",
        "Regelmäßig: Power Query.",
        "Lebendig: TEXTTEILEN oder TEXTVOR/TEXTNACH.",
      ],
    },
  ],
};
